import React from "react";
import { useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";
import { rtiActDocuments } from "../../data/rtiDocuments";
import PdfDocumentTable from "./PdfDocumentTable";
import RajyaSabha from "./RajyaSabha";
import LokSabha from "./LokSabha";
import ChapterTwoContent from "./ChapterTwoContent";
import ChapterFourContent from "./ChapterFourContent";
import ChapterTenContent from "./ChapterTenContent";
import ChapterTwelveContent from "./ChapterTwelveContent";
import ChapterThirteenContent from "./ChapterThirteenContent";
import ChapterEighteenContent from "./ChapterEighteenContent";

const actionPanels = [
  {
    title: "Action Taken by Nodal Officer",
    columns: [
      { label: "Total Online Receipt(s)", value: "27" },
      { label: "Transfer to other Public Authority", value: "2" },
      { label: "Forward to CPIO", value: "25" },
      { label: "Returned to Applicant", value: "0" },
      { label: "Disposed of", value: "0" },
      { label: "Pending", value: "0" },
    ],
  },
  {
    title: "Action Taken by CPIO(s)",
    columns: [
      { label: "Physical Receipt(s)", value: "27" },
      { label: "Forwarded by Nodal Officer", value: "2" },
      { label: "Total Receipt(s)", value: "25" },
      { label: "Disposed of", value: "0" },
      { label: "Pending", value: "0" },
    ],
  },
];

const parliamentSections = [
  {
    serial: 1,
    title: "Rajya Sabha",
    path: "/right-to-information/rajya-sabha",
  },
  {
    serial: 2,
    title: "Lok Sabha",
    path: "/right-to-information/lok-sabha",
  },
];

const ActionPanel = ({ title, columns }) => (
  <section className="rounded-2xl bg-white px-5 pb-4 pt-3 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
    <h2 className="mb-2 text-center text-[15px] font-bold text-[#006461]">
      {title}
    </h2>
    <div className="overflow-hidden rounded-lg">
      <div
        className="grid bg-[#dce9fb] text-center text-[13px] font-bold text-[#001b33]"
        style={{
          gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
        }}
      >
        {columns.map((column) => (
          <div
            key={column.label}
            className="flex min-h-[58px] items-center justify-center px-3 py-3"
          >
            {column.label}
          </div>
        ))}
      </div>
      <div
        className="grid bg-white text-center text-[13px] font-semibold text-[#16376c]"
        style={{
          gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
        }}
      >
        {columns.map((column) => (
          <div key={column.label} className="px-3 py-2">
            {column.value}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ParliamentQuestionAnswers = () => {
  const navigate = useNavigate();

  return (
    <section className="min-w-0 flex-1 rounded-2xl bg-white p-4 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
      <div className="overflow-hidden rounded-xl border border-[#d9e4ef]">
        <div className="overflow-x-auto">
          <table
            className="w-full table-fixed border-collapse text-[13px] text-[#001b33]"
            style={{ minWidth: "680px" }}
          >
            <colgroup>
              <col className="w-[72px]" />
              <col />
              <col className="w-[150px]" />
            </colgroup>
            <thead>
              <tr className="bg-[#dce9fb] text-left text-[14px] font-bold text-[#001b33]">
                <th className="px-4 py-3">S.No.</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3 text-center">Take Action</th>
              </tr>
            </thead>
            <tbody>
              {parliamentSections.map((section, index) => (
                <tr
                  key={section.path}
                  className={`border-b border-[#d9e4ef] last:border-b-0 ${
                    index % 2 === 0 ? "bg-[#fbfdff]" : "bg-[#f5f9fc]"
                  }`}
                >
                  <td className="px-4 py-2.5 align-middle font-medium">
                    {String(section.serial).padStart(2, "0")}
                  </td>
                  <td className="px-4 py-2.5 align-middle font-medium">
                    {section.title}
                  </td>
                  <td className="px-4 py-2.5 text-center align-middle">
                    <button
                      type="button"
                      onClick={() => navigate(section.path)}
                      aria-label={`Open ${section.title}`}
                      className="mx-auto inline-flex items-center gap-2 rounded-[3px] text-[#16376c] outline-none transition-colors hover:text-[#0f8a7e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f8a7e] focus-visible:ring-offset-2"
                    >
                      <span className="text-[15px] font-medium underline underline-offset-2">
                        Open
                      </span>
                      <ArrowRight size={23} strokeWidth={2.2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const RightToInformationAct = () => (
  <div className="min-w-0 flex-1 space-y-3">
    {actionPanels.map((panel) => (
      <ActionPanel key={panel.title} {...panel} />
    ))}

    <PdfDocumentTable documents={rtiActDocuments} minWidth="820px" />
  </div>
);

const RtiContent = ({ activeSection }) => {
  if (activeSection === "parliament-question-answers") {
    return <ParliamentQuestionAnswers />;
  }

  if (activeSection === "rajya-sabha") {
    return <RajyaSabha />;
  }

  if (activeSection === "lok-sabha") {
    return <LokSabha />;
  }

  if (activeSection === "chapter-2") {
    return <ChapterTwoContent />;
  }

  if (activeSection === "chapter-4") {
    return <ChapterFourContent />;
  }

  if (activeSection === "chapter-10") {
    return <ChapterTenContent />;
  }

  if (activeSection === "chapter-12") {
    return <ChapterTwelveContent />;
  }

  if (activeSection === "chapter-13") {
    return <ChapterThirteenContent />;
  }

  if (activeSection === "chapter-18") {
    return <ChapterEighteenContent />;
  }

  return <RightToInformationAct />;
};

export default RtiContent;
